import React, { useState } from 'react'
import { useNavigate } from 'react-router-dom'
import { useSubscription, useQuotaStatus, useSelectedAccountId } from '../../hooks/useSubscription'
import './SubscriptionWidget.css'

/**
 * Reusable quota display item
 */
const QuotaItem = ({ icon, label, quota }) => (
  <div className="quota-item">
    <div className="quota-label">
      <span className="quota-name">
        <span className="quota-icon">{icon}</span>
        {label}
      </span>
      <span className={`quota-value ${quota.isNearLimit ? 'warning' : ''} ${quota.isExceeded ? 'exceeded' : ''}`}>
        {quota.unlimited ? (
          <span className="unlimited">∞</span>
        ) : (
          `${quota.used}/${quota.limit}`
        )}
      </span>
    </div>
    {!quota.unlimited && (
      <div className="quota-bar-container">
        <div
          className={`quota-bar ${quota.isNearLimit ? 'warning' : ''} ${quota.isExceeded ? 'exceeded' : ''}`}
          style={{ width: `${Math.min(quota.percentage, 100)}%` }}
        />
      </div>
    )}
  </div>
)

/**
 * Dashboard widget showing subscription status and quota usage
 *
 * Phase 3: Now shows subscription and quotas for selected account
 */
const SubscriptionWidget = () => {
  const accountId = useSelectedAccountId()
  const { plan, loading, error, mutate } = useSubscription(accountId)
  const storiesQuota = useQuotaStatus('stories', accountId)
  const regenQuota = useQuotaStatus('regenerations', accountId)
  const ecomQuota = useQuotaStatus('ecommerce_stories', accountId)
  const navigate = useNavigate()

  const [isRefreshing, setIsRefreshing] = useState(false)

  const handleRefresh = async () => {
    setIsRefreshing(true)
    try {
      await mutate()
    } catch (err) {
      console.error('Failed to refresh subscription:', err)
    } finally {
      setTimeout(() => setIsRefreshing(false), 500)
    }
  }

  if (loading || storiesQuota.loading || regenQuota.loading || ecomQuota.loading) {
    return (
      <div className="subscription-widget loading">
        <div className="skeleton-header"></div>
        <div className="skeleton-bar"></div>
        <div className="skeleton-bar"></div>
        <div className="skeleton-button"></div>
      </div>
    )
  }

  if (error) {
    return (
      <div className="subscription-widget error">
        <p className="error-message">Failed to load subscription</p>
        <button className="btn-danger" onClick={() => window.location.reload()}>
          Retry
        </button>
      </div>
    )
  }

  const planName = (plan || 'free').toUpperCase()
  const planClass = `plan-${(plan || 'free').toLowerCase()}`

  return (
    <div className={`subscription-widget ${planClass}`}>
      <div className="widget-header">
        <div className="widget-header-left">
          <h3 className="plan-title">{planName} Plan</h3>
          <button
            onClick={handleRefresh}
            disabled={isRefreshing}
            className={`refresh-button ${isRefreshing ? 'refreshing' : ''}`}
            title="Refresh quota"
          >
            <svg
              width="16"
              height="16"
              viewBox="0 0 24 24"
              fill="none"
              stroke="currentColor"
              strokeWidth="2"
              strokeLinecap="round"
              strokeLinejoin="round"
            >
              <path d="M21.5 2v6h-6M2.5 22v-6h6M2 11.5a10 10 0 0 1 18.8-4.3M22 12.5a10 10 0 0 1-18.8 4.3" />
            </svg>
          </button>
        </div>
        <div className={`plan-badge ${planClass}`}>{planName}</div>
      </div>

      <div className="widget-body">
        <QuotaItem icon="📱" label="Stories" quota={storiesQuota} />
        <QuotaItem icon="✨" label="Regenerations" quota={regenQuota} />
        <QuotaItem icon="🛒" label="Produkt Stories" quota={ecomQuota} />
      </div>

      <div className="widget-footer">
        <button
          className="btn-primary"
          onClick={() => navigate('/subscription')}
        >
          {plan === 'free' ? '⬆️ Upgrade Plan' : '⚙️ Manage Plan'}
        </button>
      </div>
    </div>
  )
}

export default SubscriptionWidget
